import { Head, useForm } from "@inertiajs/react"
import { type FormEvent } from "react"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { accountInvitationsPath, settingsAccountPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Account settings", href: settingsAccountPath() },
]

interface Member {
  id: number
  name: string
  email: string
  role: string
}

interface PendingInvitation {
  id: number
  email: string
  created_at: string
}

interface Props {
  account: { id: number; name: string }
  members: Member[]
  pending_invitations: PendingInvitation[]
}

export default function Show({ account, members, pending_invitations }: Props) {
  const { data, setData, post, processing, errors, reset } = useForm({ email: "" })

  function handleInvite(e: FormEvent) {
    e.preventDefault()
    post(accountInvitationsPath(), {
      onSuccess: () => reset(),
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Account settings" />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title={account.name}
            description="Manage your household members"
          />

          <div>
            <h3 className="mb-3 text-sm font-medium">Members</h3>
            <ul className="divide-y rounded-lg border" role="list">
              {members.map((member) => (
                <li key={member.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">{member.name}</p>
                    <p className="text-muted-foreground text-sm">{member.email}</p>
                  </div>
                  <span className="text-muted-foreground text-xs capitalize">{member.role}</span>
                </li>
              ))}
            </ul>
          </div>

          {pending_invitations.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-medium">Pending invitations</h3>
              <ul className="divide-y rounded-lg border" role="list">
                {pending_invitations.map((invitation) => (
                  <li key={invitation.id} className="flex items-center justify-between px-4 py-3">
                    <p className="text-sm">{invitation.email}</p>
                    <span className="text-muted-foreground text-xs">Pending</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h3 className="mb-3 text-sm font-medium">Invite someone</h3>
            <form onSubmit={handleInvite} className="flex gap-2">
              <div className="flex-1">
                <Label htmlFor="email" className="sr-only">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={data.email}
                  onChange={(e) => setData("email", e.target.value)}
                  placeholder="Email address"
                  required
                />
                <InputError messages={errors.email} />
              </div>
              <Button type="submit" disabled={processing}>
                Send invite
              </Button>
            </form>
          </div>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
